import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Loader2, Plus, Trash2, Check, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PRIORITY_META, STATUS_META, fmtDeadline, fmtDur } from "@/lib/ui";

const COLUMNS = ["inbox", "today", "this_week", "planned", "later", "waiting", "done"];
const PRIORITIES = ["URGENT", "IMPORTANT", "QUICK_WIN", "THIS_WEEK", "CAN_WAIT"];

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", project: "", priority: "THIS_WEEK", deadline: "", estimated_minutes: 45, status: "today" });

  const load = useCallback(async () => {
    const res = await api.get("/tasks");
    setTasks(res.data.tasks);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const move = async (t, status) => {
    setTasks((prev) => prev.map((x) => x.id === t.id ? { ...x, status } : x));
    await api.patch(`/tasks/${t.id}`, { status });
  };
  const remove = async (t) => {
    setTasks((prev) => prev.filter((x) => x.id !== t.id));
    await api.delete(`/tasks/${t.id}`);
    toast.success("Tâche supprimée");
  };
  const create = async () => {
    if (!form.title.trim()) { toast.error("Titre requis"); return; }
    const payload = { ...form, estimated_minutes: Number(form.estimated_minutes) || 45, project: form.project || "Autre", deadline: form.deadline || null };
    const res = await api.post("/tasks", payload);
    setTasks((prev) => [res.data, ...prev]);
    setCreating(false);
    setForm({ title: "", project: "", priority: "THIS_WEEK", deadline: "", estimated_minutes: 45, status: "today" });
    toast.success("Tâche créée");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Tâches</h1>
          <p className="text-muted-foreground mt-1 text-sm">Ton todo central. L'IA recommande, tu gardes le contrôle.</p>
        </div>
        <button data-testid="tasks-new-button" onClick={() => setCreating(true)}
          className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm text-primary-foreground font-medium hover:opacity-90 transition-all">
          <Plus className="w-4 h-4" /> Nouvelle tâche
        </button>
      </div>

      {creating && (
        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
          data-testid="tasks-create-form" className="rounded-2xl border border-border bg-card p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-medium">Nouvelle tâche</h3>
            <button onClick={() => setCreating(false)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
          </div>
          <input autoFocus data-testid="task-input-title" placeholder="Que faut-il faire ?" value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="w-full bg-secondary border border-border rounded-xl px-4 py-2.5 text-sm" />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <input data-testid="task-input-project" placeholder="Projet" value={form.project}
              onChange={(e) => setForm({ ...form, project: e.target.value })}
              className="bg-secondary border border-border rounded-xl px-3 py-2 text-sm" />
            <select data-testid="task-input-priority" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}
              className="bg-secondary border border-border rounded-xl px-3 py-2 text-sm">
              {PRIORITIES.map((p) => <option key={p} value={p}>{PRIORITY_META[p].label}</option>)}
            </select>
            <input data-testid="task-input-deadline" type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })}
              className="bg-secondary border border-border rounded-xl px-3 py-2 text-sm" />
            <input data-testid="task-input-minutes" type="number" placeholder="min" value={form.estimated_minutes}
              onChange={(e) => setForm({ ...form, estimated_minutes: e.target.value })}
              className="bg-secondary border border-border rounded-xl px-3 py-2 text-sm" />
          </div>
          <button data-testid="task-create-submit" onClick={create}
            className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2 text-sm text-primary-foreground font-medium hover:opacity-90">
            <Check className="w-4 h-4" /> Créer
          </button>
        </motion.div>
      )}

      {loading ? (
        <div className="h-[40vh] flex items-center justify-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {COLUMNS.map((col) => {
            const items = tasks.filter((t) => t.status === col);
            return (
              <div key={col} data-testid={`tasks-column-${col}`} className="rounded-2xl border border-border bg-card/40 p-4">
                <div className="flex items-center justify-between mb-3 px-1">
                  <div className="font-display text-sm font-semibold">{STATUS_META[col].label}</div>
                  <span className="font-mono text-xs text-muted-foreground">{items.length}</span>
                </div>
                <div className="space-y-2.5 min-h-[40px]">
                  {items.map((t) => {
                    const p = PRIORITY_META[t.priority] || PRIORITY_META.CAN_WAIT;
                    return (
                      <motion.div key={t.id} layout initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                        data-testid={`task-card-${t.id}`}
                        className={`rounded-xl border border-border bg-card p-3.5 group ${t.status === "done" ? "opacity-60" : ""}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className={`text-sm font-medium ${t.status === "done" ? "line-through" : ""}`}>{t.title}</div>
                          <button data-testid={`task-delete-${t.id}`} onClick={() => remove(t)}
                            className="text-muted-foreground/0 group-hover:text-muted-foreground hover:!text-rose-400 transition-colors shrink-0">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          <span className={`font-mono text-[9px] px-2 py-0.5 rounded-full border ${p.cls}`}>{p.label}</span>
                          {t.project && t.project !== "Autre" && <span className="text-[11px] text-muted-foreground">{t.project}</span>}
                          {t.deadline && <span className="text-[11px] text-muted-foreground">· {fmtDeadline(t.deadline)}</span>}
                          {t.estimated_minutes && <span className="text-[11px] text-muted-foreground">· {fmtDur(t.estimated_minutes)}</span>}
                        </div>
                        <select data-testid={`task-move-${t.id}`} value={t.status} onChange={(e) => move(t, e.target.value)}
                          className="mt-2.5 w-full bg-secondary/60 border border-border rounded-lg px-2 py-1 text-[11px] text-muted-foreground">
                          {COLUMNS.map((c) => <option key={c} value={c}>{STATUS_META[c].label}</option>)}
                        </select>
                      </motion.div>
                    );
                  })}
                  {items.length === 0 && <div className="text-xs text-muted-foreground/60 px-1 py-2">—</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
